import React, {
  FunctionComponent, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import cloneDeep from 'lodash.clonedeep';
import { RootState } from 'data/root.reducers';
import * as globalSelectors from 'global/global-selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as collectionFunctionGroupSelectors from 'data/collectionFunctionGroup/collectionFunctionGroup.selectors';
import * as collectionFunctionActions from 'data/collectionFunction/collectionFunction.actions';
import * as userSelectors from 'users/users-selectors';
import * as contactSelectors from 'data/contact/contact.selectors';
import * as templateSelectors from 'data/template/template.selectors';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as cfFtpInfoSelectors from 'data/cfFtpInfo/cfFtpInfo.selectors';
import * as cfFtpInfoActions from 'data/cfFtpInfo/cfFtpInfo.actions';
import * as networkSelectors from 'data/network/network.selectors';
import * as securityInfoSelectors from 'data/securityInfo/securityInfo.selectors';
import * as fqdnGroupSelectors from 'data/fqdnGroup/fqdnGroup.selectors';
import * as warrantSelectors from 'data/warrant/warrant.selectors';
import * as nodeSelectors from 'data/nodes/nodes.selectors';
import * as locationActions from 'data/location/location.actions';
import * as locationSelectors from 'data/location/location.selectors';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { FormTemplate } from 'data/template/template.types';
import { CollectionFunctionFormData, CollectionFunctionInterfaceFormData } from 'data/collectionFunction/collectionFunction.types';
import { RetrievingDataProgress } from 'components';
import CollectionFunctionForm, { CollectionFunctionFormFieldHandler, CollectionFunctionFormRef } from '../../components/CollectionFunctionForm';
import {
  adaptTemplateForInterfacesFieldUpdate,
  adaptTemplateInterfaceTransportType,
  adaptTemplateName,
  adaptTemplateOptions,
  adaptTemplateVisibilityMode,
  adaptTemplate5GFields,
  adaptTemplateInterfaceMdfNodeName,
  adaptTemplateTimezoneOptions,
  adaptTemplateInterfaceFields,
  adaptTemplateStoreAndForward,
  adaptTemplateFtpInfoId,
  adaptTemplateSecinfoidFields,
  adaptLocationField,
  adaptTemplatesBasedOnAppSettings,
} from '../../utils/collectionFunctionTemplate.adapter';
import getCollectionFunctionDataToSubmit from '../../utils/getCollectionFunctionDataToSubmit';
import { createCollectionFunctionBasedOnTemplate } from '../../utils/collectionFunctionTemplate.factory';
import { handleInterfacesFieldUpdate } from '../../utils/handleInterfacesFieldUpdate';
import { handleInterfacesMdfNodeNameFieldUpdate } from '../../utils/handleInterfacesMdfNodeNameFieldUpdate';
import { handleCollectionFunctionNameUpdate } from '../../utils/handleCollectionFunctionNameUpdate';
import CollectionFunctionFormMissingTemplates from '../../components/CollectionFunctionFormMissingTemplates';
import { useFieldCreateNew } from '../../hooks/useFieldCreateNew';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  timezones: userSelectors.getTimezones(state),
  contacts: contactSelectors.getContacts(state),
  cfFtpInfos: cfFtpInfoSelectors.getCfFtpInfoList(state),
  collectionFunctionGroupList: collectionFunctionGroupSelectors.getCollectionFunctionGroupList(state),
  collectionFunctionTemplates: templateSelectors.getEnabledCollectionFunctionFormTemplatesByKey(state),
  enabledCollectionFunctions: supportedFeaturesSelectors.getEnabledCollectionFunctions(state),
  networks: networkSelectors.getNetworks(state),
  securityInfos: securityInfoSelectors.getSecurityInfos(state),
  fqdnGroups: fqdnGroupSelectors.getFqdnGroups(state),
  collectionFunctionNameCustomization: warrantSelectors.getCustomizationCollectionFunctionName(state),
  is5GCVcpEnabled: supportedFeaturesSelectors.is5gcVcpEnabled(state),
  xcipioNodes: nodeSelectors.getNodes(state),
  isCollectionFunctionGroupLoading: collectionFunctionGroupSelectors.isCollectionFunctionGroupLoading(state),
  countries: locationSelectors.getCountryList(state),
  stateNameByCountry: locationSelectors.getStateListByCountry(state),
  filteredCities: locationSelectors.getFilteredCityList(state),
  isLocationRequired: discoverySelectors.isLocationRequired(state),
  isT1678HITlsEnabled: discoverySelectors.isT1678HITlsEnabled(state),
  is33108HITlsEnabled: discoverySelectors.is33108HITlsEnabled(state),
});

const mapDispatch = {
  createCollectionFunction: collectionFunctionActions.createCollectionFunction,
  goToCollectionFunctionList: collectionFunctionActions.goToCollectionFunctionList,
  getCfFtpInfoList: cfFtpInfoActions.getCfFtpInfoList,
  fetchLocationList: locationActions.fetchLocationList,
  fetchLocationByFilterWithViewCities: locationActions.fetchLocationByFilterWithViewCities,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>
type Props = PropsFromRedux & PropsFromRouter;

export const CollectionFunctionCreate: FunctionComponent<Props> = ({
  deliveryFunctionId,
  history,
  isAuditEnabled,
  userId,
  userName,
  timezones,
  contacts,
  cfFtpInfos,
  collectionFunctionGroupList,
  collectionFunctionTemplates,
  enabledCollectionFunctions,
  networks,
  securityInfos,
  fqdnGroups,
  collectionFunctionNameCustomization,
  is5GCVcpEnabled,
  xcipioNodes,
  isCollectionFunctionGroupLoading,
  countries,
  stateNameByCountry,
  filteredCities,
  isLocationRequired,
  isT1678HITlsEnabled,
  is33108HITlsEnabled,
  createCollectionFunction,
  goToCollectionFunctionList,
  getCfFtpInfoList,
  fetchLocationByFilterWithViewCities,
  fetchLocationList,
}: Props) => {
  const [templates, setTemplates] = useState<{ [key: string]: FormTemplate }>({});
  const [template, setTemplate] = useState<FormTemplate>();
  const [data, setData] = useState<Partial<CollectionFunctionFormData>>({});
  const formRef = useRef<CollectionFunctionFormRef>(null);
  const [areTemplatesMissing, setTemplatesMissing] = useState(false);
  const [handleFieldCreateNew] = useFieldCreateNew(
    (param, formSection, tabId) => handleFieldUpdate(param, formSection, tabId),
  );
  const { t } = useTranslation();

  useEffect(() => {
    getCfFtpInfoList(deliveryFunctionId);
    fetchLocationList();
  }, [deliveryFunctionId, getCfFtpInfoList, fetchLocationList]);

  useEffect(() => {
    let availableTemplates = adaptTemplateOptions(
      collectionFunctionTemplates,
      timezones,
      contacts,
      collectionFunctionGroupList,
      cfFtpInfos,
      networks,
      securityInfos,
      fqdnGroups,
      enabledCollectionFunctions,
      xcipioNodes,
      countries,
    );

    availableTemplates = adaptTemplatesBasedOnAppSettings(
      availableTemplates,
      isLocationRequired,
      isT1678HITlsEnabled,
      is33108HITlsEnabled,
    )

    setTemplates(availableTemplates);

    if (Object.keys(availableTemplates).length === 0) {
      setTemplatesMissing(true);
    }

    let formData = data as CollectionFunctionFormData;

    const templateKey = data.tag ?? Object.keys(availableTemplates)[0];
    let adaptedTemplate = adaptTemplateVisibilityMode(availableTemplates[templateKey], 'create');
    adaptedTemplate = adaptTemplate5GFields(adaptedTemplate, is5GCVcpEnabled);
    adaptedTemplate = adaptTemplateSecinfoidFields(adaptedTemplate, isT1678HITlsEnabled, is33108HITlsEnabled);

    if (formData.tag == null) {
      formData = createCollectionFunctionBasedOnTemplate(adaptedTemplate) as CollectionFunctionFormData;
      setData(formData);
    }

    adaptedTemplate = adaptTemplateTimezoneOptions(adaptedTemplate, formData, timezones);
    adaptedTemplate = adaptTemplateInterfaceFields(adaptedTemplate, formData);

    if (data) {
      adaptedTemplate = adaptTemplateStoreAndForward(adaptedTemplate, data.storeAndForward, formData.tag);
    }

    setTemplate(adaptedTemplate);

    if (data.tag == null) {
      const initialData = createCollectionFunctionBasedOnTemplate(adaptedTemplate);
      setData(initialData);
    }
  }, [
    collectionFunctionTemplates,
    timezones,
    contacts,
    cfFtpInfos,
    collectionFunctionGroupList,
    enabledCollectionFunctions,
    networks,
    securityInfos,
    fqdnGroups,
    xcipioNodes,
    is5GCVcpEnabled,
    countries,
  ]);

  useEffect(() => {
    if (filteredCities.length > 0 && data?.country && data?.stateName) {
      const templateData = adaptLocationField(template, filteredCities, 'city');
      setTemplate(templateData);
    }
  }, [filteredCities]);

  const handleClickSave = () => {
    const isValid = formRef?.current?.isValid(true, true) ?? true;
    if (!isValid) {
      return;
    }

    const formData = formRef?.current?.getValues();
    if (!formData) {
      return;
    }

    const collectionFunctionBody = getCollectionFunctionDataToSubmit(
      formData,
      null,
      'CREATE'
    );

    createCollectionFunction(deliveryFunctionId, collectionFunctionBody, history);
  };

  const handleCancel = () => {
    goToCollectionFunctionList(history);
  };

  const handleInterfaceAdd = (formSection: 'hi2Interfaces' | 'hi3Interfaces') => {
    const clonedData = cloneDeep(data);

    const newData = createCollectionFunctionBasedOnTemplate(template, true);
    const newInterfaceData = (newData[formSection] as CollectionFunctionInterfaceFormData[])[0];
    (clonedData[formSection] as CollectionFunctionInterfaceFormData[]).push(newInterfaceData);

    if (formSection === 'hi3Interfaces'){
      setTemplate((prevTemplate) => adaptTemplateForInterfacesFieldUpdate(
        formSection,
        clonedData,
        prevTemplate,
        'destType',
        null,
      ));
    }
    setData(clonedData);
  };

  const handleInterfaceRemove = (tabId: string | number, formSection: 'hi2Interfaces' | 'hi3Interfaces') => {
    const clonedData = cloneDeep(data);

    clonedData[formSection] = clonedData[formSection]?.filter(({ id }) => id !== tabId);

    setData(clonedData);
  };

  const handleFieldUpdate: CollectionFunctionFormFieldHandler = (
    { name: fieldName, value },
    formSection,
    interfaceId,
  ) => {
    const clonedData = cloneDeep(data);
    if (formSection === 'main' || formSection === 'facilityInformation') {
      if (fieldName === 'country') {
        const stateNames = stateNameByCountry[value]?.map(({ stateName }) => stateName) ?? [];

        let templateData = adaptLocationField(template, stateNames, 'stateName');
        templateData = adaptLocationField(templateData, [], 'city');

        setData({
          ...clonedData,
          [fieldName]: value,
          stateName: '',
          city: '',
        });
        setTemplate(templateData);

      } else if (fieldName === 'stateName') {
        const { country = '' } = clonedData;
        const { stateCode } = stateNameByCountry[country]?.find(({ stateName }) => stateName === value) ?? {};

        if (country && stateCode) {
          fetchLocationByFilterWithViewCities(`country eq '${country}' and stateCode eq '${stateCode}'`);
        }

        const templateData = adaptLocationField(template, [], 'city');

        setData({
          ...clonedData,
          [fieldName]: value,
          city: '',
        });
        setTemplate(templateData);

      } else if (fieldName === 'storeAndForward') {
        const adaptedTemplate = adaptTemplateFtpInfoId(template, fieldName, value);
        setData({
          ...clonedData,
          ftpId: '',
          [fieldName]: value,
        });
        setTemplate(adaptedTemplate);
      } else if (fieldName === 'cfGroup') {
        const name = handleCollectionFunctionNameUpdate(
          collectionFunctionNameCustomization,
          collectionFunctionGroupList,
          clonedData,
          fieldName,
          value,
        );

        setData({
          ...clonedData,
          name,
          [fieldName]: value,
        });
        setTemplate((prevTemplate) => adaptTemplateName(prevTemplate, name));

      } else if (fieldName === 'tag') {
        let adaptedTemplate = adaptTemplateVisibilityMode(templates[value], 'create');
        adaptedTemplate = adaptTemplateSecinfoidFields(adaptedTemplate, isT1678HITlsEnabled, is33108HITlsEnabled);
        adaptedTemplate = adaptTemplate5GFields(adaptedTemplate, is5GCVcpEnabled);

        const initialData = createCollectionFunctionBasedOnTemplate(adaptedTemplate);

        const name = handleCollectionFunctionNameUpdate(
          collectionFunctionNameCustomization,
          collectionFunctionGroupList,
          clonedData,
          fieldName,
          value,
        );
        adaptedTemplate = adaptTemplateName(adaptedTemplate, name);
        adaptedTemplate = adaptTemplateStoreAndForward(adaptedTemplate, false, value);
        const { cfGroup } = clonedData;

        // When changing the type, only the name and cfGroup are preserved
        setData({
          ...initialData,
          name,
          cfGroup,
        });
        setTemplate(adaptedTemplate);

      } else if (fieldName === 'deliveryThru') {
        if (value === 'AF_DELIVERY') {
          clonedData.hi2Interfaces = [];
          clonedData.hi3Interfaces = [];
        } else {
          const newData = createCollectionFunctionBasedOnTemplate(template);
          clonedData.hi2Interfaces = newData.hi2Interfaces;
          clonedData.hi3Interfaces = newData.hi3Interfaces;
        }
        setData({
          ...clonedData,
          [fieldName]: value,
        });
      } else {
        setData({
          ...clonedData,
          [fieldName]: value,
        });
      }

    } else if (formSection === 'hi2Interfaces' || formSection === 'hi3Interfaces') {
      if (fieldName === 'transportType' && clonedData[formSection]?.[0].transportType !== value) {
        setTemplate((prevTemplate) => {
          // Changing the collection function interface transport type should reset the interfaces
          const adaptedTemplate = adaptTemplateInterfaceTransportType(prevTemplate, formSection, value);
          const initialData = createCollectionFunctionBasedOnTemplate(adaptedTemplate, true);

          clonedData[formSection] = initialData[formSection] as CollectionFunctionInterfaceFormData[];

          setData(clonedData);
          return adaptedTemplate;
        });

      } else if (fieldName === 'mdf2NodeName' || fieldName === 'mdf3NodeName') {
        // Adapt the template so all new created interfaces have the same value
        const adaptedTemplate = adaptTemplateInterfaceMdfNodeName(template, formSection, fieldName, value);
        // Set the mdf node name to all interfaces
        clonedData[formSection] = handleInterfacesMdfNodeNameFieldUpdate(
          clonedData[formSection],
          fieldName,
          value,
        );

        setData(clonedData);
        setTemplate(adaptedTemplate);

      } else {
        clonedData[formSection] = handleInterfacesFieldUpdate(
          clonedData[formSection],
          interfaceId,
          fieldName,
          value,
        );

        setData(clonedData);
        setTemplate((prevTemplate) => adaptTemplateForInterfacesFieldUpdate(
          formSection,
          clonedData,
          prevTemplate,
          fieldName,
          value,
        ));
      }
    }
  };

  if (areTemplatesMissing) {
    return (
      <CollectionFunctionFormMissingTemplates
        title={t('collectionFunction.create.title')}
        onClickCancelClose={handleCancel}
      />
    );
  }

  if (!template) {
    return null;
  }

  return (
    <div className="app-container-content" data-testid="CollectionFunctionCreate">
      <CollectionFunctionForm
        ref={formRef}
        title={t('collectionFunction.create.title')}
        isEditing
        data={data}
        template={template}
        onClickSave={handleClickSave}
        onClickCancelClose={handleCancel}
        onFieldUpdate={handleFieldUpdate}
        onFieldCreateNew={handleFieldCreateNew}
        onInterfaceAdd={handleInterfaceAdd}
        onInterfaceRemove={handleInterfaceRemove}
      />
      <RetrievingDataProgress isOpen={isCollectionFunctionGroupLoading} message={t('collectionFunction.savingLea')} />
    </div>
  );
};

export default withRouter(connector(CollectionFunctionCreate));
